// Suggested spots for a trip: past catches (yours, and spots friends shared to your groups) grouped into spots, each
// scored by how much the weather and time when they were caught look like now. Pure functions, no screen: see
// trip.ts for where the inputs come from, and test/spots.test.ts.
import { distanceKm } from "../../shared/geo.ts";

/** One past catch with a place, and the conditions when it was caught (null = not known). */
export interface Sample {
  lat: number;
  lon: number;
  time: number;
  wind: number | null;
  windDir: number | null;
  /** Pressure change over 3 hours, hPa. */
  pressure3h: number | null;
  water: number | null;
  /** 1 for your own catches; less for catches friends shared (their conditions are less certain for you). */
  weight: number;
  /** Who shared it, for catches from groups. */
  sharedBy?: string;
}
/** The conditions now (or at the planned time). */
export interface Conditions {
  time: number;
  wind: number | null;
  windDir: number | null;
  pressure3h: number | null;
  water: number | null;
}

/** What made a spot a good match: the same time of year, time of day, wind, wind direction, pressure trend, water temperature. */
export type Reason = "season" | "hour" | "wind" | "dir" | "trend" | "water";
const WEIGHTS: Record<Reason, number> = { season: 2, hour: 1, wind: 1, dir: 1, trend: 1, water: 1.5 };

export interface Spot {
  lat: number;
  lon: number;
  samples: Sample[];
  /** How alike the conditions are, 0–1 (weighted average over the spot's catches). */
  match: number;
  /** For ranking: more similar catches = higher. */
  score: number;
  /** The strongest reasons, best first. */
  reasons: Reason[];
  /** Distance from where you are, km (when known). */
  km?: number;
}

const circular = (a: number, b: number, period: number) => { const d = Math.abs(a - b) % period; return Math.min(d, period - d); };
const trendOf = (d: number) => (d <= -1 ? "falling" : d >= 1 ? "rising" : "steady");
const dayOfYear = (t: number) => { const d = new Date(t); return (Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) - Date.UTC(d.getFullYear(), 0, 0)) / 864e5; };

/** How alike one catch's conditions are to now, 0–1, and each part that could be compared. */
export function similarity(s: Sample, now: Conditions): { value: number; parts: Partial<Record<Reason, number>> } {
  const parts: Partial<Record<Reason, number>> = {};
  // within about two months either side of the same date counts, fading out
  parts.season = Math.max(0, 1 - circular(dayOfYear(s.time), dayOfYear(now.time), 365) / 60);
  parts.hour = Math.max(0, 1 - circular(new Date(s.time).getHours(), new Date(now.time).getHours(), 24) / 6);
  if (s.wind != null && now.wind != null) parts.wind = Math.max(0, 1 - Math.abs(s.wind - now.wind) / 6);
  // direction only matters when there is some wind
  if (s.windDir != null && now.windDir != null && (s.wind ?? 0) >= 2 && (now.wind ?? 0) >= 2) parts.dir = 1 - circular(s.windDir, now.windDir, 360) / 180;
  if (s.pressure3h != null && now.pressure3h != null) parts.trend = trendOf(s.pressure3h) === trendOf(now.pressure3h) ? 1 : 0.3;
  if (s.water != null && now.water != null) parts.water = Math.max(0, 1 - Math.abs(s.water - now.water) / 6);
  let sum = 0, weights = 0;
  for (const [k, v] of Object.entries(parts) as [Reason, number][]) { sum += v * WEIGHTS[k]; weights += WEIGHTS[k]; }
  return { value: weights ? sum / weights : 0, parts };
}

export interface SuggestOptions {
  /** Catches closer together than this are one spot, metres. */
  radiusM?: number;
  max?: number;
  /** Where you are: spots get a distance, and those farther than maxKm are left out. */
  from?: { lat: number; lon: number };
  maxKm?: number;
}

/** The best spots for the conditions, best first. */
export function suggestSpots(samples: Sample[], now: Conditions, { radiusM = 300, max = 5, from, maxKm = 150 }: SuggestOptions = {}): Spot[] {
  // group catches into spots: each joins the first spot whose middle is within radiusM
  const groups: { lat: number; lon: number; samples: Sample[] }[] = [];
  for (const s of [...samples].sort((a, b) => b.time - a.time)) {
    const g = groups.find((x) => distanceKm(x.lat, x.lon, s.lat, s.lon) * 1000 <= radiusM);
    if (g) {
      g.samples.push(s);
      g.lat += (s.lat - g.lat) / g.samples.length; // running average of the positions
      g.lon += (s.lon - g.lon) / g.samples.length;
    } else groups.push({ lat: s.lat, lon: s.lon, samples: [s] });
  }
  const spots: Spot[] = groups.map((g) => {
    let sum = 0, weights = 0;
    const partSum: Partial<Record<Reason, number>> = {}, partN: Partial<Record<Reason, number>> = {};
    for (const s of g.samples) {
      const sim = similarity(s, now);
      sum += sim.value * s.weight;
      weights += s.weight;
      for (const [k, v] of Object.entries(sim.parts) as [Reason, number][]) { partSum[k] = (partSum[k] ?? 0) + v; partN[k] = (partN[k] ?? 0) + 1; }
    }
    const reasons = (Object.keys(partSum) as Reason[])
      .map((k) => ({ k, v: partSum[k]! / partN[k]! }))
      .filter((r) => r.v >= 0.7)
      .sort((a, b) => b.v * WEIGHTS[b.k] - a.v * WEIGHTS[a.k])
      .slice(0, 3).map((r) => r.k);
    return { lat: g.lat, lon: g.lon, samples: g.samples, match: weights ? sum / weights : 0, score: sum, reasons,
      km: from ? distanceKm(from.lat, from.lon, g.lat, g.lon) : undefined };
  });
  return spots
    .filter((s) => s.km == null || s.km <= maxKm)
    .sort((a, b) => b.score - a.score)
    .slice(0, max);
}
